/**
 * Country-name -> ISO-3166-1 alpha-2 lookup.
 *
 * battleface's quote/order API requires ISO-2 country codes (host_country,
 * country_code) and a state/province code for US / CA / CH. Our sign-up stores
 * the country as a display name, so we map it here. If a value is already a
 * 2-letter code it is returned upper-cased; unknown values return "".
 */

const NAME_TO_ISO2 = {
  afghanistan: "AF", albania: "AL", algeria: "DZ", andorra: "AD", angola: "AO",
  "antigua and barbuda": "AG", argentina: "AR", armenia: "AM", australia: "AU",
  austria: "AT", azerbaijan: "AZ", bahamas: "BS", bahrain: "BH", bangladesh: "BD",
  barbados: "BB", belarus: "BY", belgium: "BE", belize: "BZ", benin: "BJ",
  bhutan: "BT", bolivia: "BO", "bosnia and herzegovina": "BA", botswana: "BW",
  brazil: "BR", brunei: "BN", bulgaria: "BG", "burkina faso": "BF", burundi: "BI",
  cambodia: "KH", cameroon: "CM", canada: "CA", "cape verde": "CV",
  "central african republic": "CF", chad: "TD", chile: "CL", china: "CN",
  colombia: "CO", comoros: "KM", congo: "CG", "democratic republic of the congo": "CD",
  "costa rica": "CR", "cote d'ivoire": "CI", "ivory coast": "CI", croatia: "HR",
  cuba: "CU", cyprus: "CY", "czech republic": "CZ", czechia: "CZ", denmark: "DK",
  djibouti: "DJ", dominica: "DM", "dominican republic": "DO", ecuador: "EC",
  egypt: "EG", "el salvador": "SV", "equatorial guinea": "GQ", eritrea: "ER",
  estonia: "EE", eswatini: "SZ", swaziland: "SZ", ethiopia: "ET", fiji: "FJ",
  finland: "FI", france: "FR", gabon: "GA", gambia: "GM", georgia: "GE",
  germany: "DE", ghana: "GH", greece: "GR", grenada: "GD", guatemala: "GT",
  guinea: "GN", "guinea-bissau": "GW", guyana: "GY", haiti: "HT", honduras: "HN",
  "hong kong": "HK", hungary: "HU", iceland: "IS", india: "IN", indonesia: "ID",
  iran: "IR", iraq: "IQ", ireland: "IE", israel: "IL", italy: "IT", jamaica: "JM",
  japan: "JP", jordan: "JO", kazakhstan: "KZ", kenya: "KE", kiribati: "KI",
  kosovo: "XK", kuwait: "KW", kyrgyzstan: "KG", laos: "LA", latvia: "LV",
  lebanon: "LB", lesotho: "LS", liberia: "LR", libya: "LY", liechtenstein: "LI",
  lithuania: "LT", luxembourg: "LU", macau: "MO", madagascar: "MG", malawi: "MW",
  malaysia: "MY", maldives: "MV", mali: "ML", malta: "MT", "marshall islands": "MH",
  mauritania: "MR", mauritius: "MU", mexico: "MX", micronesia: "FM", moldova: "MD",
  monaco: "MC", mongolia: "MN", montenegro: "ME", morocco: "MA", mozambique: "MZ",
  myanmar: "MM", burma: "MM", namibia: "NA", nauru: "NR", nepal: "NP",
  netherlands: "NL", "new zealand": "NZ", nicaragua: "NI", niger: "NE",
  nigeria: "NG", "north korea": "KP", "north macedonia": "MK", macedonia: "MK",
  norway: "NO", oman: "OM", pakistan: "PK", palau: "PW", palestine: "PS",
  panama: "PA", "papua new guinea": "PG", paraguay: "PY", peru: "PE",
  philippines: "PH", poland: "PL", portugal: "PT", "puerto rico": "PR", qatar: "QA",
  romania: "RO", russia: "RU", "russian federation": "RU", rwanda: "RW",
  "saint kitts and nevis": "KN", "saint lucia": "LC",
  "saint vincent and the grenadines": "VC", samoa: "WS", "san marino": "SM",
  "sao tome and principe": "ST", "saudi arabia": "SA", senegal: "SN", serbia: "RS",
  seychelles: "SC", "sierra leone": "SL", singapore: "SG", slovakia: "SK",
  slovenia: "SI", "solomon islands": "SB", somalia: "SO", "south africa": "ZA",
  "south korea": "KR", korea: "KR", "south sudan": "SS", spain: "ES",
  "sri lanka": "LK", sudan: "SD", suriname: "SR", sweden: "SE", switzerland: "CH",
  syria: "SY", taiwan: "TW", tajikistan: "TJ", tanzania: "TZ", thailand: "TH",
  "timor-leste": "TL", "east timor": "TL", togo: "TG", tonga: "TO",
  "trinidad and tobago": "TT", tunisia: "TN", turkey: "TR", turkiye: "TR",
  turkmenistan: "TM", tuvalu: "TV", uganda: "UG", ukraine: "UA",
  "united arab emirates": "AE", uae: "AE", "united kingdom": "GB", uk: "GB",
  "great britain": "GB", england: "GB", scotland: "GB", wales: "GB",
  "northern ireland": "GB", "united states": "US", "united states of america": "US",
  usa: "US", "u.s.a.": "US", america: "US", uruguay: "UY", uzbekistan: "UZ",
  vanuatu: "VU", "vatican city": "VA", venezuela: "VE", vietnam: "VN", yemen: "YE",
  zambia: "ZM", zimbabwe: "ZW",
};

/** Countries where battleface requires a state/province code. */
const STATE_REQUIRED = new Set(["US", "CA", "CH"]);

/** Normalise a country value (name or code) to an ISO-3166-1 alpha-2 code. */
export function toIso2(value) {
  if (!value) return "";
  const v = String(value).trim();
  if (/^[A-Za-z]{2}$/.test(v)) return v.toUpperCase();
  const key = v.toLowerCase().replace(/\s+/g, " ");
  return NAME_TO_ISO2[key] || "";
}

/** True when the given country (name or ISO-2) needs a state_code. */
export function needsStateCode(value) {
  return STATE_REQUIRED.has(toIso2(value));
}
